<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from '@/composables/useToast';
import {
  getProjectItemAssignments,
  returnProjectItemAssignment,
  getProject,
} from '@/api/api';
import BasePageHeader from '@/components/base/BasePageHeader.vue';
import BaseConfirmModal from '@/components/base/BaseConfirmModal.vue';
import EmptyState from '@/components/base/EmptyState.vue';
import MdiAccountArrowRight from '~icons/mdi/account-arrow-right';
import MdiPackageVariantClosed from '~icons/mdi/package-variant-closed';
import MdiArrowULeftTop from '~icons/mdi/arrow-u-left-top';

const route  = useRoute();
const router = useRouter();
const toast  = useToast();
const { t } = useI18n();

const projectId   = Number(route.params.projectId);
const projectName = ref('');
const assignments = ref([]);
const loading     = ref(true);
const error       = ref(null);

// Return from person modal
const returnTarget  = ref(null);  // assignment object
const returning     = ref(false);

async function load() {
  loading.value = true;
  error.value   = null;
  try {
    const [projectRes, assignRes] = await Promise.all([
      getProject(projectId),
      getProjectItemAssignments({ project: projectId, is_active: 'true', page_size: 200 }),
    ]);
    projectName.value = projectRes.name ?? t('ui.projectNumber', { id: projectId });
    assignments.value = (assignRes.results ?? assignRes ?? []);
  } catch (e) {
    error.value = t('projectPersonCheckouts.loadFailed');
  } finally {
    loading.value = false;
  }
}

async function confirmFieldReturn() {
  if (!returnTarget.value) return;
  returning.value = true;
  try {
    await returnProjectItemAssignment(returnTarget.value.id);
    toast.success(`${returnTarget.value.person_name} ${t('projectPersonCheckouts.returnedItem')}`);
    assignments.value = assignments.value.filter(a => a.id !== returnTarget.value.id);
    returnTarget.value = null;
  } catch {
    toast.error(t('projectPersonCheckouts.returnFailed'));
  } finally {
    returning.value = false;
  }
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('no-NO', { dateStyle: 'short', timeStyle: 'short' });
}

onMounted(load);
</script>

<template>
  <div class="page-view">
    <BasePageHeader :title="`${projectName} — ${$t('projectPersonCheckouts.title')}`" @back="router.back()">
      <template #actions>
        <button class="btn ghost small" @click="load">{{ $t('common.refresh') }}</button>
      </template>
    </BasePageHeader>

    <div class="list-stack pt-4">
      <div v-if="loading" class="state-empty">{{ $t('common.loading') }}</div>
      <EmptyState v-else-if="error" :message="error" variant="error" />
      <EmptyState
        v-else-if="!assignments.length"
        :message="$t('projectPersonCheckouts.noItems')"
      />

      <template v-else>
        <p class="section-label px-2">
          {{ assignments.length }} {{ assignments.length !== 1 ? $t('projectPersonCheckouts.itemsWith') : $t('projectPersonCheckouts.itemWith') }}
        </p>

        <div
          v-for="a in assignments"
          :key="a.id"
          class="data-card"
        >
          <div class="data-card__main">
            <div class="data-card__name flex items-center gap-2">
              <MdiPackageVariantClosed class="w-4 h-4 text-amber-400 shrink-0" />
              {{ a.item_metadata_name ?? t('ui.checkoutNumber', { id: a.item_checkout }) }}
              <span v-if="a.item_metadata_asset_tag" class="badge badge-muted text-xs">
                {{ a.item_metadata_asset_tag }}
              </span>
            </div>
            <div class="data-card__meta">
              <MdiAccountArrowRight class="w-3.5 h-3.5 text-sky-400 shrink-0" />
              <span class="text-sm">{{ a.person_name }}</span>
              <span class="badge badge-accent">{{ $t('common.active') }}</span>
              <span class="text-xs" style="color:var(--color-muted)">{{ $t('projectPersonCheckouts.since') }} {{ formatDate(a.assigned_at) }}</span>
            </div>
          </div>

          <div class="data-card__actions">
            <!-- Field return: item stays on project -->
            <button
              class="btn ghost small"
              :title="$t('projectPersonCheckouts.returnFromPerson')"
              @click="returnTarget = a"
            >
              <MdiArrowULeftTop class="w-3.5 h-3.5 mr-1" />
              {{ $t('projectPersonCheckouts.returnFromPerson') }}
            </button>
          </div>
        </div>
      </template>
    </div>

    <!-- Confirm field return modal -->
    <BaseConfirmModal
      :show="!!returnTarget"
      :title="$t('projectPersonCheckouts.returnFromPersonTitle')"
      :body="$t('ui.fieldReturnBody', { person: returnTarget?.person_name, item: returnTarget?.item_metadata_name ?? $t('ui.thisItem') })"
      :confirm-label="$t('projectPersonCheckouts.returnFromPersonConfirm')"
      confirm-variant="primary"
      :loading="returning"
      @confirm="confirmFieldReturn"
      @cancel="returnTarget = null"
    />
  </div>
</template>
